import React, { useState } from "react";
import { X, Lock, RefreshCw, Zap } from "lucide-react";
import { api } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";

export default function CheckoutModal({ cart, onClose, onSuccess, billingMode = "one-time" }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const total = cart.reduce(
        (s, p) => s + parseInt(String(p.price).replace(/,/g, "") || "0", 10), 0
    );

    const isSubscription = billingMode === "subscription";
    const premiumPlan = cart.find((p) => p.tier === "premium");
    const addonPlan = cart.find((p) => p.tier === "addon");

    const checkout = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");

        const ok = await loadRazorpay();
        if (!ok) { setError("Could not load payment gateway."); setLoading(false); return; }

        try {
            if (isSubscription) {
                // Subscription flow
                if (!premiumPlan) {
                    setError("Premium plan required for subscription"); setLoading(false); return;
                }
                const { data } = await api.post("/subscriptions/create", {
                    plan_id: premiumPlan.id,
                    addon_id: addonPlan?.id || null,
                    customer_name: form.name,
                    customer_email: form.email,
                    customer_phone: form.phone,
                });
                const rzp = new window.Razorpay({
                    key: data.key_id,
                    subscription_id: data.subscription_id,
                    name: "Femcare",
                    description: data.addon_name
                        ? `${data.plan_name} + ${data.addon_name} (auto-renews)`
                        : `${data.plan_name} (auto-renews)`,
                    prefill: { name: form.name, email: form.email, contact: form.phone },
                    theme: { color: "#E91E8C" },
                    handler: async (resp) => {
                        try {
                            const v = await api.post("/subscriptions/verify", {
                                razorpay_subscription_id: resp.razorpay_subscription_id,
                                razorpay_payment_id: resp.razorpay_payment_id,
                                razorpay_signature: resp.razorpay_signature,
                            });
                            const sub = v.data.subscription || {};
                            const total = (sub.plan_price || 0) + (sub.addon_price || 0);
                            onSuccess({
                                receipt: sub.razorpay_subscription_id,
                                amount_inr: total,
                                customer_name: sub.customer_name,
                                customer_email: sub.customer_email,
                                items: [
                                    sub.plan_name && { name: sub.plan_name, price: sub.plan_price, period: "billing cycle" },
                                    sub.addon_name && { name: sub.addon_name, price: sub.addon_price, period: "billing cycle" },
                                ].filter(Boolean),
                                is_subscription: true,
                            });
                        } catch (err) {
                            setError("Subscription verification failed.");
                            setLoading(false);
                        }
                    },
                    modal: { ondismiss: () => setLoading(false) },
                });
                rzp.on("payment.failed", (resp) => {
                    setError(resp.error?.description || "Payment failed");
                    setLoading(false);
                });
                rzp.open();
            } else {
                // One-time order flow
                const { data } = await api.post("/payments/create-order", {
                    items: cart.map((p) => ({
                        plan_id: p.id, name: p.name,
                        price: parseInt(String(p.price).replace(/,/g, ""), 10),
                        period: p.period, tier: p.tier,
                    })),
                    customer_name: form.name,
                    customer_email: form.email,
                    customer_phone: form.phone,
                });
                const rzp = new window.Razorpay({
                    key: data.key_id,
                    amount: data.amount,
                    currency: data.currency,
                    name: "Femcare",
                    description: cart.map((c) => c.name).join(" + "),
                    order_id: data.order_id,
                    prefill: { name: form.name, email: form.email, contact: form.phone },
                    theme: { color: "#E91E8C" },
                    handler: async (response) => {
                        try {
                            const verify = await api.post("/payments/verify", {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            });
                            onSuccess(verify.data.order);
                        } catch {
                            setError("Payment verification failed.");
                            setLoading(false);
                        }
                    },
                    modal: { ondismiss: () => setLoading(false) },
                });
                rzp.on("payment.failed", (resp) => {
                    setError(resp.error?.description || "Payment failed");
                    setLoading(false);
                });
                rzp.open();
            }
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not start checkout");
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-femcare-purple/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="checkout-modal">
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-femcare-border">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent inline-flex items-center gap-1.5">
                            {isSubscription ? <RefreshCw className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                            {isSubscription ? "Subscription · Auto-renews" : "One-time secure checkout"}
                        </p>
                        <h3 className="font-serif text-2xl text-femcare-purple mt-1">Complete your order</h3>
                    </div>
                    <button onClick={onClose} className="text-femcare-purple/60 hover:text-femcare-pink" data-testid="checkout-close-btn">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 bg-femcare-petal/30 border-b border-femcare-border">
                    <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-3">Your cart</p>
                    <div className="space-y-2 mb-4">
                        {cart.map((c) => (
                            <div key={c.id} className="flex justify-between items-center text-sm" data-testid={`cart-row-${c.id}`}>
                                <span className="text-femcare-purple">{c.name} <span className="text-femcare-purple/50 text-xs">/{c.period}</span></span>
                                <span className="font-medium text-femcare-purple">₹{c.price}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-femcare-border">
                        <span className="font-serif text-lg text-femcare-purple">
                            {isSubscription ? "First charge" : "Total"}
                        </span>
                        <span className="font-serif text-2xl text-femcare-pink" data-testid="checkout-total">₹{total.toLocaleString("en-IN")}</span>
                    </div>
                    {isSubscription && (
                        <p className="text-[11px] text-femcare-purple/60 mt-2 leading-snug">
                            Auto-renews every {premiumPlan?.period || "billing cycle"}. Cancel anytime from your account.
                        </p>
                    )}
                </div>

                <form onSubmit={checkout} className="p-6 space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Full name</label>
                        <input required value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                            data-testid="checkout-name" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Email</label>
                            <input type="email" required value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="checkout-email" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Phone</label>
                            <input type="tel" required value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="9000000000"
                                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="checkout-phone" />
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-600" data-testid="checkout-error">{error}</p>}

                    <button type="submit" disabled={loading}
                        className="w-full btn-primary justify-center disabled:opacity-50"
                        data-testid="checkout-pay-btn">
                        <Lock className="w-4 h-4" />
                        {loading ? "Processing..." :
                            isSubscription
                                ? `Subscribe · pay ₹${total.toLocaleString("en-IN")} now`
                                : `Pay ₹${total.toLocaleString("en-IN")} securely`}
                    </button>

                    <p className="text-[11px] text-center text-femcare-purple/50">
                        Powered by Razorpay · UPI · Cards · Netbanking · Wallets
                    </p>
                </form>
            </div>
        </div>
    );
}
